import { getConfig } from "@stryker/core/api/jobs";
import { QueryKeys } from "@stryker/core/queryKeys";
import type { JobDetailsData, RepairOptionsSection } from "@stryker/core/types/compositeSections";
import type { ExpandStatus } from "@stryker/core/types/expandable";
import type { ExpandableCards } from "@stryker/core/types/jobs";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { groupBy, prop, uniqueBy } from "remeda";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Typography } from "@/components/ui/typography";
import ExpandableSection from "../../../../components/job/ExpandableSection";

const cardName = "repairOptions";

type RepairOptionsProps = {
  savedRepairOptions: RepairOptionsSection;
  toggleExpandableSection: (section: ExpandableCards) => void;
  setRepairOptions: (payload: { data: { repair_length_options: string[] }; status: ExpandStatus }) => void;
  jobDetailsData: JobDetailsData;
};

export function RepairOptions({ toggleExpandableSection, savedRepairOptions, setRepairOptions }: RepairOptionsProps) {
  const { data: configResponse } = useQuery({
    queryKey: [QueryKeys.JobsConfig],
    queryFn: getConfig,
    staleTime: 1000000,
  });

  // Same item name can appear multiple times with different units (e.g. per gallon vs per quart).
  // Unit doesn't matter for this checklist, so we only keep the first occurrence of each name.
  const groupedRepairOptions = configResponse
    ? Object.entries(groupBy(uniqueBy(configResponse.repair_options, prop("name")), prop("type")))
    : [];

  // Initialize state, will be updated via useEffect when config loads
  const [repair_length_options, setRepair_length_options] = useState<string[]>([]);

  // The backend returns/expects option names (strings). Older saved jobs may
  // still have numeric IDs persisted, so map those back to names using the config.
  useEffect(() => {
    if (!savedRepairOptions.isDataFilled || !configResponse) {
      return;
    }

    const savedOptions = savedRepairOptions.data.repair_length_options as Array<string | number>;

    const mappedNames = savedOptions
      .map((optionNameOrId) => {
        if (typeof optionNameOrId === "string") {
          return optionNameOrId;
        }

        // Legacy numeric ID - resolve it to the option's name.
        const foundOption = configResponse.repair_options.find((opt) => opt.id === optionNameOrId);
        return foundOption?.name;
      })
      .filter((name): name is string => typeof name === "string");

    if (mappedNames.length > 0) {
      setRepair_length_options(mappedNames);
    }
  }, [savedRepairOptions.isDataFilled, savedRepairOptions.data.repair_length_options, configResponse]);

  function handleSave() {
    setRepairOptions({
      data: { repair_length_options },
      status: "done",
    });
  }

  return (
    <ExpandableSection
      status={savedRepairOptions.status}
      setIsExpanded={() => toggleExpandableSection(cardName)}
      title="Repair Options"
    >
      <Typography variant="text-small" className="mb-4 text-gray-600">
        Check all or ones you would like to add if any
      </Typography>
      <div className="mb-4 flex flex-col gap-4">
        {groupedRepairOptions.map(([key, options], index) => {
          return (
            <CollapsibleOptions
              key={key}
              label={key}
              index={index}
              options={options}
              repair_length_options={repair_length_options}
              setRepair_length_options={setRepair_length_options}
            />
          );
        })}
        <Button className="ml-auto self-end" onClick={handleSave}>
          Save
        </Button>
      </div>
    </ExpandableSection>
  );
}

type CollapsibleOptionsProps = {
  options: Array<{ id: number; name: string; type: string }>;
  repair_length_options: string[];
  setRepair_length_options: (updater: (prev: string[]) => string[]) => void;
  label: string;
  index: number;
};

function CollapsibleOptions({
  label,
  options,
  index,
  setRepair_length_options,
  repair_length_options,
}: CollapsibleOptionsProps) {
  const isCustomLabel = label.toLowerCase() === "custom";
  return (
    <div>
      <Typography className="mb-2 flex w-full cursor-pointer items-center justify-between gap-2">
        {index + 1}. {label}
      </Typography>
      <div className="grid grid-cols-2 gap-2 pl-4 md:grid-cols-3">
        {options.map((option) => {
          function handleCheckboxChange(value: boolean) {
            setRepair_length_options((prevState) => {
              if (isCustomLabel) {
                return value ? [...prevState, option.name] : prevState.filter((v) => v !== option.name);
              }
              const otherSelected = prevState.filter((v) => !options.some((opt) => opt.name === v));
              return value ? [...otherSelected, option.name] : otherSelected;
            });
          }

          return (
            <Checkbox
              key={option.id}
              checked={repair_length_options.includes(option.name)}
              onChange={handleCheckboxChange}
              label={option.name}
            />
          );
        })}
      </div>
    </div>
  );
}
